// src/components/AbastecimentoLoteModal.jsx
import api from '../services/api';
import { toast } from 'react-toastify';
import React, { useState, useMemo, useCallback } from 'react';
import {
  FaGasPump,
  FaTimes,
  FaPlus,
  FaTrash,
  FaSpinner,
  FaFileExcel,
  FaDownload,
  FaUpload,
  FaTable,
  FaLayerGroup,
  FaCheckCircle,
  FaExclamationTriangle,
} from 'react-icons/fa';
import './AbastecimentoLoteModal.css';

const TIPOS_COMBUSTIVEL = ['Gasolina', 'Etanol', 'Diesel', 'Diesel S-10', 'GNV'];

const formatCurrency = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const normalizeDriverId = (id) => {
  if (id === null || id === undefined || id === '') return '';
  return String(id).replace('MOT-', '').replace('USR-', '').trim();
};

const getNowLocalDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
};

let _rowSeq = 0;
const novaLinha = (veiculo) => ({
  key: `row_${++_rowSeq}`,
  veiculo_id: veiculo.id,
  placa: veiculo.placa,
  modelo: `${veiculo.marca || ''} ${veiculo.modelo || ''}`.trim(),
  litros: '',
  valor: '',
  odometro_registrado: '',
  tipo_combustivel: '',
  motorista_id: '',
});

export default function AbastecimentoLoteModal({
  veiculos = [],
  motoristas = [],
  preSelecionados = [],
  onClose,
  onSuccess,
}) {
  const [aba, setAba] = useState('manual');
  const [dataPadrao, setDataPadrao] = useState(getNowLocalDate());

  const [linhas, setLinhas] = useState(() => {
    if (!preSelecionados?.length) return [];
    return veiculos
      .filter((v) => preSelecionados.includes(v.id))
      .map((v) => novaLinha(v));
  });

  const [veiculoParaAdd, setVeiculoParaAdd] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Importação
  const [importFormato, setImportFormato] = useState('blb'); // 'blb' | 'sem_parar'
  const [arquivo, setArquivo] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);

  const motoristasNormalizados = useMemo(
    () =>
      (motoristas || []).map((m) => ({
        ...m,
        id_normalizado: normalizeDriverId(m.id),
        label_extra:
          m.origem === 'operacional'
            ? ' (Operacional)'
            : m.origem === 'motorista'
            ? ' (Motorista)'
            : '',
        optionKey: m.id_raw || m.id,
      })),
    [motoristas]
  );

  const idsNaGrade = useMemo(() => new Set(linhas.map((l) => l.veiculo_id)), [linhas]);

  const veiculosDisponiveis = useMemo(
    () => veiculos.filter((v) => !idsNaGrade.has(v.id)),
    [veiculos, idsNaGrade]
  );

  const totais = useMemo(() => {
    const litros = linhas.reduce((a, l) => a + (parseFloat(l.litros) || 0), 0);
    const valor = linhas.reduce((a, l) => a + (parseFloat(l.valor) || 0), 0);
    return { litros, valor, qtd: linhas.length };
  }, [linhas]);

  const addLinha = useCallback(
    (veiculoId) => {
      const v = veiculos.find((x) => String(x.id) === String(veiculoId));
      if (!v) return;
      setLinhas((prev) => [...prev, novaLinha(v)]);
    },
    [veiculos]
  );

  const addTodos = useCallback(() => {
    setLinhas((prev) => {
      const existentes = new Set(prev.map((l) => l.veiculo_id));
      const novos = veiculos.filter((v) => !existentes.has(v.id)).map((v) => novaLinha(v));
      return [...prev, ...novos];
    });
  }, [veiculos]);

  const removeLinha = useCallback((key) => {
    setLinhas((prev) => prev.filter((l) => l.key !== key));
  }, []);

  const updateLinha = useCallback((key, field, value) => {
    setLinhas((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  }, []);

  const handleSubmitManual = async () => {
    if (linhas.length === 0) {
      toast.warning('Adicione ao menos um veículo na grade.');
      return;
    }
    if (!dataPadrao) {
      toast.warning('Informe a data do lançamento.');
      return;
    }

    const invalidas = linhas.filter((l) => {
      const litrosNum = parseFloat(l.litros);
      const valorNum = parseFloat(l.valor);
      return !Number.isFinite(litrosNum) || litrosNum <= 0 || !Number.isFinite(valorNum) || valorNum <= 0;
    });

    if (invalidas.length > 0) {
      toast.warning(
        `Preencha litros e valor (> 0) em ${invalidas.length} veículo(s): ${invalidas
          .map((l) => l.placa)
          .join(', ')}.`
      );
      return;
    }

    const abastecimentos = linhas.map((l) => ({
      veiculo_id: l.veiculo_id,
      data_abastecimento: dataPadrao,
      litros: parseFloat(l.litros),
      valor: parseFloat(l.valor),
      odometro_registrado: l.odometro_registrado || null,
      tipo_combustivel: l.tipo_combustivel || null,
      motorista_id: l.motorista_id ? Number(normalizeDriverId(l.motorista_id)) : null,
    }));

    setEnviando(true);
    try {
      const { data } = await api.post('/api/vehicles/abastecimentos/lote', { abastecimentos });
      const ignorados = data?.ignorados || 0;
      toast.success(
        `${data?.inseridos ?? abastecimentos.length} abastecimento(s) lançado(s)!${
          ignorados ? ` ${ignorados} ignorado(s).` : ''
        }`
      );
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Falha ao lançar abastecimentos em lote.');
    } finally {
      setEnviando(false);
    }
  };

  const handleImport = async () => {
    if (!arquivo) {
      toast.warning('Selecione um arquivo para importar.');
      return;
    }

    setImportando(true);
    setResultadoImport(null);
    try {
      const fd = new FormData();
      fd.append('file', arquivo);

      const endpoint =
        importFormato === 'sem_parar'
          ? '/api/vehicles/abastecimentos/import-sem-parar'
          : '/api/vehicles/abastecimentos/import';

      const { data } = await api.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResultadoImport(data);

      if ((data?.inseridos ?? 0) > 0) {
        toast.success(data?.message || 'Importação concluída!');
        onSuccess?.();
      } else {
        toast.warning('Nenhuma linha foi importada. Confira os detalhes.');
      }
    } catch (error) {
      const resp = error?.response?.data;
      setResultadoImport({
        inseridos: 0,
        duplicados: 0,
        ignorados: resp?.detalhes?.length || 0,
        detalhes: resp?.detalhes || [],
      });
      toast.error(resp?.error || 'Falha ao importar planilha.');
    } finally {
      setImportando(false);
    }
  };

  const baixarModelo = () => {
    const linhasCsv = [
      'placa,data,litros,valor,odometro,tipo_combustivel,posto,observacao',
      `ABC1D23,${getNowLocalDate()},45.50,280.00,158420,Diesel S-10,Posto BR Rod. SP-330,Abastecimento de rota`,
      `XYZ2E34,${getNowLocalDate()},38.00,235.00,92010,Gasolina,Posto Shell Centro,`,
    ].join('\n');

    const blob = new Blob([`\uFEFF${linhasCsv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo_abastecimentos.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="abl-overlay" onClick={onClose}>
      <div className="abl-modal" onClick={(e) => e.stopPropagation()}>
        <header className="abl-header">
          <div className="abl-title">
            <div className="abl-icon-box">
              <FaGasPump />
            </div>
            <div>
              <h2>Abastecimento em Massa</h2>
              <span className="abl-sub">Lance combustível para vários veículos de uma vez</span>
            </div>
          </div>
          <button onClick={onClose} className="abl-btn-close" aria-label="Fechar">
            <FaTimes />
          </button>
        </header>

        <div className="abl-tabs">
          <button
            className={`abl-tab ${aba === 'manual' ? 'active' : ''}`}
            onClick={() => setAba('manual')}
          >
            <FaTable /> Grade manual
          </button>
          <button
            className={`abl-tab ${aba === 'import' ? 'active' : ''}`}
            onClick={() => setAba('import')}
          >
            <FaFileExcel /> Importar planilha
          </button>
        </div>

        <div className="abl-body">
          {aba === 'manual' && (
            <div className="abl-manual">
              <div className="abl-toolbar">
                <div className="abl-input-group abl-data">
                  <label>Data do lançamento</label>
                  <input
                    type="date"
                    value={dataPadrao}
                    onChange={(e) => setDataPadrao(e.target.value)}
                  />
                </div>

                <div className="abl-input-group abl-add">
                  <label>Adicionar veículo</label>
                  <div className="abl-add-row">
                    <select
                      value={veiculoParaAdd}
                      onChange={(e) => {
                        if (e.target.value) {
                          addLinha(e.target.value);
                          setVeiculoParaAdd('');
                        }
                      }}
                    >
                      <option value="">
                        {veiculosDisponiveis.length > 0
                          ? 'Selecione um veículo...'
                          : 'Todos já adicionados'}
                      </option>
                      {veiculosDisponiveis.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.placa} — {v.marca} {v.modelo}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="abl-btn-ghost"
                      onClick={addTodos}
                      disabled={veiculosDisponiveis.length === 0}
                      title="Adicionar todos os veículos"
                    >
                      <FaLayerGroup /> Todos
                    </button>
                  </div>
                </div>
              </div>

              {linhas.length === 0 ? (
                <div className="abl-empty">
                  <FaGasPump />
                  <strong>Nenhum veículo na grade</strong>
                  <span>Adicione veículos acima para começar o lançamento em massa.</span>
                </div>
              ) : (
                <div className="abl-grid-scroll">
                  <table className="abl-grid">
                    <thead>
                      <tr>
                        <th className="abl-col-veic">Veículo</th>
                        <th>Litros *</th>
                        <th>Valor (R$) *</th>
                        <th>Odômetro (km)</th>
                        <th>Combustível</th>
                        <th>Motorista</th>
                        <th aria-label="Ações" />
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map((l) => (
                        <tr key={l.key}>
                          <td className="abl-col-veic">
                            <span className="abl-veic-placa">{l.placa}</span>
                            <span className="abl-veic-modelo">{l.modelo}</span>
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              value={l.litros}
                              onChange={(e) => updateLinha(l.key, 'litros', e.target.value)}
                              placeholder="0.00"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              className="abl-input-money"
                              value={l.valor}
                              onChange={(e) => updateLinha(l.key, 'valor', e.target.value)}
                              placeholder="0.00"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={l.odometro_registrado}
                              onChange={(e) =>
                                updateLinha(l.key, 'odometro_registrado', e.target.value)
                              }
                              placeholder="—"
                            />
                          </td>
                          <td>
                            <select
                              value={l.tipo_combustivel}
                              onChange={(e) => updateLinha(l.key, 'tipo_combustivel', e.target.value)}
                            >
                              <option value="">—</option>
                              {TIPOS_COMBUSTIVEL.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              value={l.motorista_id}
                              onChange={(e) => updateLinha(l.key, 'motorista_id', e.target.value)}
                            >
                              <option value="">—</option>
                              {motoristasNormalizados.map((m) => (
                                <option key={m.optionKey} value={m.id_normalizado}>
                                  {m.nome}
                                  {m.label_extra}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="abl-col-acao">
                            <button
                              type="button"
                              className="abl-icon-btn delete"
                              title="Remover veículo"
                              onClick={() => removeLinha(l.key)}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {aba === 'import' && (
            <div className="abl-import">
              {/* Seletor de formato — Modelo BLB / Extrato Sem Parar */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                <button
                  type="button"
                  className={importFormato === 'blb' ? 'abl-btn-primary' : 'abl-btn-outline'}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => {
                    setImportFormato('blb');
                    setResultadoImport(null);
                  }}
                >
                  <FaFileExcel /> Modelo BLB
                </button>
                <button
                  type="button"
                  className={importFormato === 'sem_parar' ? 'abl-btn-primary' : 'abl-btn-outline'}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => {
                    setImportFormato('sem_parar');
                    setResultadoImport(null);
                  }}
                >
                  <FaGasPump /> Extrato Sem Parar
                </button>
              </div>

              <div className="abl-import-info">
                {importFormato === 'blb' ? (
                  <>
                    <h4>Como importar</h4>
                    <p>
                      Baixe o modelo, preencha uma linha por abastecimento e suba o arquivo. As placas
                      precisam existir na frota; linhas com erro são ignoradas e listadas no final.
                    </p>
                    <code className="abl-cols">
                      placa · data · litros · valor · odometro · tipo_combustivel · posto · observacao
                    </code>

                    <button type="button" className="abl-btn-outline" onClick={baixarModelo}>
                      <FaDownload /> Baixar modelo (.csv)
                    </button>
                  </>
                ) : (
                  <>
                    <h4>Extrato Sem Parar</h4>
                    <p>
                      Não precisa formatar nada: envie o <strong>extrato original do Sem Parar</strong>{' '}
                      (.xlsx ou .csv). O sistema reconhece o cabeçalho sozinho e importa{' '}
                      <strong>apenas as linhas de abastecimento</strong> — pedágio e estacionamento são
                      ignorados. Cada veículo é vinculado pela <strong>placa</strong> do extrato, e
                      transações repetidas já importadas são ignoradas automaticamente.
                    </p>
                  </>
                )}
              </div>

              <label className={`abl-dropzone ${arquivo ? 'has-file' : ''}`}>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    setArquivo(e.target.files?.[0] || null);
                    setResultadoImport(null);
                  }}
                />
                <FaUpload className="abl-dropzone-icon" />
                <span className="abl-dropzone-text">
                  {arquivo ? arquivo.name : 'Clique para escolher a planilha (.xlsx / .csv)'}
                </span>
                {arquivo && (
                  <button
                    type="button"
                    className="abl-remove-file"
                    onClick={(e) => {
                      e.preventDefault();
                      setArquivo(null);
                      setResultadoImport(null);
                    }}
                  >
                    <FaTimes /> Remover
                  </button>
                )}
              </label>

              {resultadoImport && (
                <div className="abl-result">
                  <div className="abl-result-stats">
                    <div className="abl-result-stat ok">
                      <FaCheckCircle /> {resultadoImport.inseridos || 0} importados
                    </div>
                    {(resultadoImport.duplicados || 0) > 0 && (
                      <div className="abl-result-stat warn">
                        <FaExclamationTriangle /> {resultadoImport.duplicados} duplicados
                      </div>
                    )}
                    {(resultadoImport.ignorados || 0) > 0 && (
                      <div className="abl-result-stat warn">
                        <FaExclamationTriangle /> {resultadoImport.ignorados} ignorados
                      </div>
                    )}
                  </div>

                  {Array.isArray(resultadoImport.detalhes) && resultadoImport.detalhes.length > 0 && (
                    <ul className="abl-result-erros">
                      {resultadoImport.detalhes.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="abl-footer">
          {aba === 'manual' ? (
            <>
              <div className="abl-totais">
                <div className="abl-total">
                  <span className="abl-total-label">Veículos</span>
                  <strong>{totais.qtd}</strong>
                </div>
                <div className="abl-total">
                  <span className="abl-total-label">Litros</span>
                  <strong>{totais.litros.toFixed(2)} L</strong>
                </div>
                <div className="abl-total">
                  <span className="abl-total-label">Total</span>
                  <strong className="abl-total-money">{formatCurrency(totais.valor)}</strong>
                </div>
              </div>

              <div className="abl-footer-actions">
                <button type="button" className="abl-btn-cancel" onClick={onClose} disabled={enviando}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="abl-btn-primary"
                  onClick={handleSubmitManual}
                  disabled={enviando || linhas.length === 0}
                >
                  {enviando ? (
                    <>
                      <FaSpinner className="fa-spin" /> Lançando...
                    </>
                  ) : (
                    <>
                      <FaPlus /> Lançar {totais.qtd > 0 ? `${totais.qtd} ` : ''}abastecimento(s)
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="abl-footer-actions abl-footer-actions--end">
              <button type="button" className="abl-btn-cancel" onClick={onClose} disabled={importando}>
                Fechar
              </button>
              <button
                type="button"
                className="abl-btn-primary"
                onClick={handleImport}
                disabled={importando || !arquivo}
              >
                {importando ? (
                  <>
                    <FaSpinner className="fa-spin" /> Importando...
                  </>
                ) : (
                  <>
                    <FaUpload /> Importar {importFormato === 'sem_parar' ? 'Sem Parar' : 'planilha'}
                  </>
                )}
              </button>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}